<template>
  <section class="overview-page">
    <div class="page-header">
      <p class="eyebrow">AI DOS Control Plane</p>
      <h2>Admin Overview</h2>
      <p>
        AI DOS admin tools help verify calculation-backed recommendations before
        candidate generation, ranking, AgentCore orchestration, and enrichment
        go live.
      </p>
    </div>

    <div class="feature-grid">
      <Card v-for="feature in features" :key="feature.title">
        <template #title>{{ feature.title }}</template>
        <template #subtitle>{{ feature.status }}</template>
        <template #content>
          <p>{{ feature.description }}</p>
        </template>
      </Card>
    </div>
  </section>
</template>

<script setup lang="ts">
const features = [
  {
    title: "Sizing Parity",
    status: "Available now",
    description:
      "Run random completed ESP onshore sizings through direct browser calculations and the Falcon sizing service."
  },
  {
    title: "Material Search & Candidates",
    status: "Available now",
    description:
      "Search Spyglass catalog materials and generate full-package, calculation-verified ESP candidates with trace-backed audit output."
  },
  {
    title: "ERP Material Mapping",
    status: "Available now",
    description:
      "Review confirmed one-to-many mappings between Spyglass catalog records and SAP material numbers, including optional ERP material-master validation."
  },
  {
    title: "Ranking Validation",
    status: "Available now",
    description:
      "Resolve a completed sizing, generate verified candidates, and inspect deterministic Performance, Run-life, and Cost ranking output."
  },
  {
    title: "Calculation Tester & Trace Operations",
    status: "Available now",
    description:
      "Run ad-hoc sizing calculations for any scenario, pre-flight scenario context for missing data, and inspect persisted calculation traces by trace ID."
  },
  {
    title: "AgentCore Admin",
    status: "Available now",
    description:
      "Inspect the AWS-hosted AgentCore runtime and Gateway, invoke the runtime stub, list MCP tools, and run targeted tool-call smoke tests from the admin UI."
  },
  {
    title: "Golden Fixture Matrix",
    status: "Scripted",
    description:
      "Track normal, high-gas, Y-tool, missing-data, and overload fixtures for repeatable parity validation."
  },
  {
    title: "AI DOS Settings",
    status: "Planned",
    description:
      "Monitor feature flags, thresholds, model/runtime integrations, and enrichment source availability."
  }
];
</script>

<style scoped lang="scss">
.page-header {
  max-width: 52rem;
  margin-bottom: 1.5rem;

  h2 {
    margin: 0 0 0.5rem;
    font-size: 2rem;
  }

  p:last-child {
    color: var(--text-color-secondary);
    line-height: 1.5;
  }
}

.eyebrow {
  color: var(--red-500);
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  margin: 0 0 0.25rem;
  text-transform: uppercase;
}

.feature-grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(16rem, 1fr));
}
</style>
